import React from "react";
// eslint-disable-next-line import/no-unresolved
import Files from "react-files";
import { cn } from "../../lib/cn";

export interface FileDropProps {
  accepts?: string[];
  maxFileSize?: number;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
  onError?: (error: { code: number; message: string }) => void;
  title?: string;
  hint?: string;
  variant?: "dropzone" | "image";
  previewUrl?: string;
  className?: string;
}

const zoneBase =
  "cursor-pointer rounded-[14px] border-[1.5px] border-dashed border-border bg-white/[.015] transition-[border-color,background] duration-[180ms] hover:border-cyan hover:bg-cyan/[0.14]";

/**
 * Dropzone over react-files. `variant="dropzone"` = the dashed audio/file zone;
 * `variant="image"` = a square artwork zone that shows `previewUrl` once picked.
 */
export const FileDrop: React.FC<FileDropProps> = ({
  accepts,
  maxFileSize = 100_000_000,
  multiple = false,
  onFiles,
  onError,
  title = "Drop files here or click to upload",
  hint,
  variant = "dropzone",
  previewUrl,
  className,
}) => {
  const common = {
    onChange: (files: File[]) => onFiles(files),
    onError,
    accepts,
    multiple,
    maxFileSize,
    minFileSize: 0,
    clickable: true,
  };

  if (variant === "image") {
    return (
      <Files
        {...common}
        className={cn(zoneBase, "relative grid place-items-center aspect-square max-w-[180px] overflow-hidden", className)}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="artwork" className="w-full h-full object-cover" />
        ) : (
          <div className="text-center px-3">
            <div className="mx-auto mb-2 w-10 h-10 rounded-[12px] bg-cyan/[0.14] text-cyan grid place-items-center [&_svg]:w-5 [&_svg]:h-5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.5-3.5L9 20" /></svg>
            </div>
            {hint && <small className="text-faint text-[11.5px]">{hint}</small>}
          </div>
        )}
      </Files>
    );
  }

  return (
    <Files
      {...common}
      className={cn(zoneBase, "flex flex-col justify-center items-center text-center px-5 py-[30px]", className)}
    >
      <div className="w-[52px] h-[52px] mb-3 rounded-[14px] bg-cyan/[0.14] text-cyan grid place-items-center [&_svg]:w-[26px] [&_svg]:h-[26px]">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>
      </div>
      <div className="text-white text-[15px] font-semibold">{title}</div>
      {hint && <div className="text-muted text-[12.5px] mt-1">{hint}</div>}
    </Files>
  );
};

export default FileDrop;
